/**
 * ─── Philippine Government Contributions Calculator ──────────
 * Computes SSS, PhilHealth, and Pag-IBIG contributions based on
 * current mandated rates (2025).
 *
 * All rates are metadata-driven through PayrollConfig — tenants
 * can update rates when government mandates change without
 * code deployment.
 */

import type { PayrollConfig, Employee } from './hris.schema'
import { DEFAULT_PAYROLL_CONFIG } from './hris.schema'

// ─── SSS Contribution Table (2025) ──────────────────────────
// Based on SSS Circular 2024-001: 14% total rate
// Employee share: 4.5% of Monthly Salary Credit (MSC)
// Employer share: 9.5% of MSC
// MSC ranges from 4,000 to 30,000 (2025)

interface SSSBracket {
  salaryFrom: number
  salaryTo: number
  msc: number          // Monthly Salary Credit
  employeeShare: number
  employerShare: number
}

/** SSS Contribution Schedule — metadata-driven, configurable */
const SSS_CONTRIBUTION_TABLE: SSSBracket[] = []

for (let msc = 5000; msc <= 30000; msc += 500) {
  const isFirst = msc === 5000
  const isLast = msc === 30000
  SSS_CONTRIBUTION_TABLE.push({
    salaryFrom: isFirst ? 0 : msc - 250,
    salaryTo: isLast ? Infinity : msc + 249.99,
    msc,
    employeeShare: msc * 0.045,
    employerShare: msc * 0.095,
  })
}

// ═══════════════════════════════════════════════════════════════
// Contribution Service
// ═══════════════════════════════════════════════════════════════

export class ContributionService {
  private config: PayrollConfig

  constructor(config: PayrollConfig = DEFAULT_PAYROLL_CONFIG) {
    this.config = config
  }

  /**
   * Get SSS contribution for a given monthly salary.
   * Returns employee share only (employer share computed separately).
   */
  static getSSSContribution(monthlySalary: number): {
    msc: number
    employeeShare: number
    employerShare: number
  } {
    for (const bracket of SSS_CONTRIBUTION_TABLE) {
      if (monthlySalary <= bracket.salaryTo) {
        return {
          msc: bracket.msc,
          employeeShare: bracket.employeeShare,
          employerShare: bracket.employerShare,
        }
      }
    }
    // Max bracket
    const max = SSS_CONTRIBUTION_TABLE[SSS_CONTRIBUTION_TABLE.length - 1]!
    return {
      msc: max.msc,
      employeeShare: max.employeeShare,
      employerShare: max.employerShare,
    }
  }

  /**
   * Compute PhilHealth contribution (2025: 5% of monthly salary, split equally).
   * Salary floor: 10,000 PHP, Salary ceiling: 100,000 PHP.
   */
  static getPhilHealthContribution(monthlySalary: number): {
    employeeShare: number
    employerShare: number
  } {
    // 2024 limits
    const base = Math.min(100000, monthlySalary)
    const total = base * 0.05
    const half = Math.round(total / 2 * 100) / 100
    return {
      employeeShare: half,
      employerShare: half,
    }
  }

  /**
   * Compute Pag-IBIG contribution.
   * 100 employee + 100 employer standard; for salary > 1,500: 2% each capped at 100
   */
  static getPagIBIGContribution(
    monthlySalary: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): { employeeShare: number; employerShare: number } {
    let employeeRate = monthlySalary > 1500 ? 0.02 : 0.01
    let employerRate = monthlySalary > 1500 ? 0.02 : 0.02

    let employeeShare = monthlySalary <= 1500 ? 100 : Math.min(monthlySalary * employeeRate, 100)
    let employerShare = monthlySalary <= 1500 ? 100 : Math.min(monthlySalary * employerRate, 100)

    // User explicitly says: 100 employee + 100 employer standard; for salary > 1,500: 2% each capped at 100
    if (monthlySalary > 1500) {
      employeeShare = Math.min(monthlySalary * 0.02, 100)
      employerShare = Math.min(monthlySalary * 0.02, 100)
    } else {
      employeeShare = 100
      employerShare = 100
    }

    return {
      employeeShare: Math.round(employeeShare * 100) / 100,
      employerShare: Math.round(employerShare * 100) / 100,
    }
  }

  /**
   * Compute all three government contributions at once.
   */
  static computeAllContributions(
    monthlySalary: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): {
    sss: { employeeShare: number; employerShare: number; msc: number }
    philhealth: { employeeShare: number; employerShare: number }
    pagibig: { employeeShare: number; employerShare: number }
    totalEmployeeShare: number
    totalEmployerShare: number
  } {
    const sss = ContributionService.getSSSContribution(monthlySalary)
    const philhealth = ContributionService.getPhilHealthContribution(monthlySalary)
    const pagibig = ContributionService.getPagIBIGContribution(monthlySalary, config)

    return {
      sss: { employeeShare: sss.employeeShare, employerShare: sss.employerShare, msc: sss.msc },
      philhealth,
      pagibig,
      totalEmployeeShare: sss.employeeShare + philhealth.employeeShare + pagibig.employeeShare,
      totalEmployerShare: sss.employerShare + philhealth.employerShare + pagibig.employerShare,
    }
  }

  /**
   * Compute overtime pay per DOLE labor code.
   *
   * @param hourlyRate — Employee's hourly rate (basicSalary / 26 days / 8 hours)
   * @param overtimeHours — Number of overtime hours
   * @param rateMultiplier — e.g. 1.25 for regular OT, 1.30 for rest day, 2.00 for holiday
   */
  static computeOvertimePay(
    hourlyRate: number,
    overtimeHours: number,
    rateMultiplier: number = 1.25,
  ): number {
    return Math.round(hourlyRate * overtimeHours * rateMultiplier * 100) / 100
  }

  /**
   * Compute night shift differential (10pm-6am).
   * Additional 10% of hourly rate per hour worked between 10pm-6am.
   */
  static computeNightDifferential(
    hourlyRate: number,
    nightHours: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): number {
    return Math.round(hourlyRate * nightHours * config.nightDiffRate * 100) / 100
  }

  /**
   * Compute daily rate from monthly salary.
   * Standard: monthly / 26 (for daily-paid) or / 22 (for monthly-paid with weekends off)
   */
  static getDailyRate(monthlySalary: number, workingDaysPerMonth: number = 26): number {
    return Math.round(monthlySalary / workingDaysPerMonth * 100) / 100
  }

  /**
   * Compute hourly rate from monthly salary.
   */
  static getHourlyRate(monthlySalary: number, workingDaysPerMonth: number = 26, hoursPerDay: number = 8): number {
    return Math.round(monthlySalary / workingDaysPerMonth / hoursPerDay * 100) / 100
  }

  /**
   * Compute pro-rated salary for a partial pay period.
   * Used for new hires or resigned employees mid-period.
   */
  static computeProRatedSalary(
    monthlySalary: number,
    daysWorked: number,
    totalWorkingDaysInPeriod: number = 13,
  ): number {
    if (totalWorkingDaysInPeriod <= 0) return 0
    return Math.round(monthlySalary * (daysWorked / totalWorkingDaysInPeriod) * 100) / 100
  }

  /**
   * Compute late/absence deduction.
   */
  static computeLateDeduction(
    hourlyRate: number,
    lateMinutes: number,
  ): number {
    return Math.round(hourlyRate * (lateMinutes / 60) * 100) / 100
  }

  /**
   * Format payslip as printable text.
   */
  static formatPayslip(payslip: {
    employeeName: string
    employeeCode: string
    department: string
    position: string
    periodStart: string
    periodEnd: string
    basicPay: number
    overtimePay: number
    holidayPay: number
    nightDifferential: number
    allowance: number
    otherEarnings: number
    grossPay: number
    sssContribution: number
    philhealthContribution: number
    pagibigContribution: number
    withholdingTax: number
    sssLoan: number
    pagibigLoan: number
    cashAdvances: number
    lateDeduction: number
    absenceDeduction: number
    otherDeductions: number
    totalDeductions: number
    netPay: number
    daysWorked: number
    hoursWorked: number
    overtimeHours: number
    lateMinutes: number
  }): string {
    const nf = (n: number) => `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`

    return `
═══════════════════════════════════════════════════════════════
                       P A Y S L I P
                    ${payslip.periodStart} to ${payslip.periodEnd}
═══════════════════════════════════════════════════════════════

Employee:     ${payslip.employeeName} (${payslip.employeeCode})
Department:   ${payslip.department}
Position:     ${payslip.position}

───────────────────────────────────────────────────────────────
  EARNINGS
───────────────────────────────────────────────────────────────
  Basic Pay:              ${nf(payslip.basicPay)}
  Overtime Pay:           ${nf(payslip.overtimePay)}
  Holiday Pay:            ${nf(payslip.holidayPay)}
  Night Differential:     ${nf(payslip.nightDifferential)}
  Allowance:              ${nf(payslip.allowance)}
  Other Earnings:         ${nf(payslip.otherEarnings)}
───────────────────────────────────────────────────────────────
  GROSS PAY:              ${nf(payslip.grossPay)}
───────────────────────────────────────────────────────────────

───────────────────────────────────────────────────────────────
  DEDUCTIONS
───────────────────────────────────────────────────────────────
  SSS Contribution:       ${nf(payslip.sssContribution)}
  PhilHealth:             ${nf(payslip.philhealthContribution)}
  Pag-IBIG:               ${nf(payslip.pagibigContribution)}
  Withholding Tax:        ${nf(payslip.withholdingTax)}
  SSS Loan:               ${nf(payslip.sssLoan)}
  Pag-IBIG Loan:          ${nf(payslip.pagibigLoan)}
  Cash Advances:          ${nf(payslip.cashAdvances)}
  Late:                   ${nf(payslip.lateDeduction)}
  Absences:               ${nf(payslip.absenceDeduction)}
  Other Deductions:       ${nf(payslip.otherDeductions)}
───────────────────────────────────────────────────────────────
  TOTAL DEDUCTIONS:       ${nf(payslip.totalDeductions)}
───────────────────────────────────────────────────────────────

───────────────────────────────────────────────────────────────
  NET PAY:                ${nf(payslip.netPay)}
───────────────────────────────────────────────────────────────

Attendance:  ${payslip.daysWorked} days · ${payslip.hoursWorked} hrs
             OT: ${payslip.overtimeHours} hrs · Late: ${payslip.lateMinutes} min

═══════════════════════════════════════════════════════════════
`.trim()
  }
}
